/**
 * Display labels for each panel-side DimKey. Used by the rail's
 * applied-filter rows and pinned-inactive rows to render a stable,
 * human-readable label per dimension.
 *
 * Source of truth for the union: rates-filter-panel.service.ts (`DimKey`).
 * Per spec § 4.3 of docs/superpowers/specs/2026-05-29-rail-as-summary-design.md.
 */
import type { DimKey } from './rates-filter-panel.service';

export const DIM_LABELS: Record<DimKey, string> = {
  carrier:     'Carrier',
  amBest:      'A.M. Best',
  premiumType: 'Premium Type',
  premium:     'Premium',
  index:       'Index',
  crediting:   'Strategy',
  combo:       'Strategy Combo',
  mktg:        'Marketing',
  surrYears:   'Surrender Years',
  bonus:       'Bonus',
  inverse:     'Inverse',
  mva:         'MVA',
  rop:         'ROP',
  riders:      'Riders',
  states:      'States',
  owner:       'Owner',
  annuitant:   'Annuitant',
  faRateTerm:  'FA Rate Term',
  faSurrYears: 'FA Surrender Years',
  faBonusYears: 'FA Bonus Yrs',
  faMva:       'MVA',
  faQualified: 'Qualified',
};

/**
 * Mapping from the panel-side DimKey (22 entries) to the rail-pin DimKey
 * (19 entries). Used to query RailPinService.isPinned() for a given
 * panel dim. Returns null when no pin equivalent exists (e.g. `mktg`,
 * `faMva`, `faQualified`).
 *
 * The two unions exist because the pin service predates the panel
 * service and uses earlier names (carriers/indexes/strategies, with plurals).
 */
import type { DimKey as PinDimKey } from '../rail/state/dim-tab-applicability';

export const PANEL_DIM_TO_PIN_DIM: Partial<Record<DimKey, PinDimKey>> = {
  carrier:     'carriers',
  amBest:      'amBest',
  premiumType: 'premiumTypes',
  index:       'indexes',
  crediting:   'strategies',
  combo:       'strategyCombos',
  // mktg has no pin equivalent — Mktg Name was never independently pinnable
  premium:     'premium',
  surrYears:   'surrYrs',
  faRateTerm:  'faRateTerm',
  faSurrYears: 'faSurrYears',
  faBonusYears: 'faBonusYears',
  bonus:       'bonus',
  inverse:     'inverse',
  mva:         'mva',
  rop:         'rop',
  riders:      'riders',
  states:      'states',
  owner:       'owner',
  annuitant:   'annuitant',
  // faMva / faQualified have no pin equivalents
};
